import { computed } from 'vue';
import { SiteRouteNames, LmsRouteNames } from '~/types/types';
import type { NuxtLinkProps } from 'nuxt/app';
import { useLoginState } from '~/composables/useLoginState';

export type PdpMenuItem = {
  label: string;
  title?: string;
  to?: NuxtLinkProps['to'];
  items?: PdpMenuItem[];
  shouldRender: boolean;
  icon?: string;
};

/**
 * PDP (Polo Development Platform) Navigation
 * For learning environment pages
 */
export const usePdpNavigation = () => {
  const { isLoggedIn } = useLoginState();

  const menuItems = computed<PdpMenuItem[]>(() => [
    {
      label: 'Back to Home',
      title: 'Return to homepage',
      to: { name: SiteRouteNames.home },
      shouldRender: true,
      icon: 'pi-arrow-left',
    },
    {
      label: 'E-Learning',
      title: 'Browse courses and learning content',
      to: { name: SiteRouteNames.courses },
      shouldRender: true,
    },
    {
      label: 'Access Options',
      title: 'Subscription pathways for the Development Platform',
      shouldRender: true,
      items: [
        {
          label: 'Full Site Subscription',
          to: { name: SiteRouteNames.subscriptions },
          shouldRender: true,
        },
        {
          label: 'Premium Packages',
          to: { name: SiteRouteNames.premiumPackages },
          shouldRender: true,
        },
        {
          label: 'Premium Subscriptions',
          to: { name: SiteRouteNames.premiumSubscriptions },
          shouldRender: true,
        },
      ],
    },
    {
      label: 'Blog',
      title: 'Learning insights and educational content',
      to: { name: SiteRouteNames.blog },
      shouldRender: true,
    },
    // Logged out: "Sign In". Logged in: swap to "My Dashboard" pointing at the LMS.
    // The prerendered HTML always shows the logged-out variant; this resolves on
    // the client after hydration (see useLoginState).
    {
      label: isLoggedIn.value ? 'My Dashboard' : 'Sign In',
      title: isLoggedIn.value
        ? 'Go to your dashboard'
        : 'Sign in to your account',
      to: isLoggedIn.value
        ? { name: LmsRouteNames.allContent }
        : {
            name: LmsRouteNames.brandLogin,
            params: { portal: 'ipapolo' },
          },
      shouldRender: true,
      icon: isLoggedIn.value ? 'pi-th-large' : 'pi-sign-in',
    },
    // Only relevant to logged-out visitors.
    {
      label: 'Create a free account',
      title: 'Create a free account',
      to: {
        name: LmsRouteNames.brandLogin,
        params: { portal: 'ipapolo' },
        query: { signup: 'true' },
      },
      shouldRender: !isLoggedIn.value,
      icon: 'pi-user-plus',
    },
  ]);

  const visibleItems = computed(() => {
    return menuItems.value
      .filter((item) => item.shouldRender)
      .map((item) => {
        // Filter subitems if they exist
        if (item.items) {
          return {
            ...item,
            items: item.items.filter((subItem) => subItem.shouldRender),
          };
        }
        return item;
      })
      .filter((item) => {
        // Remove items with nested menus that have no visible subitems
        if (item.items) {
          return item.items.length > 0;
        }
        return true;
      });
  });

  return {
    menuItems,
    visibleItems,
  };
};
